import {queries} from '@/shared/integrations/zero'
import {ListActiveSubstancesInput} from '@cypro/engine/queries'
import {DrugCatalogItem} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'

export type ActiveSubstanceStartRow = Pick<DrugCatalogItem, 'id' | 'createdAt' | 'name'>

export function useGetInfiniteActiveSubstanceDrugs({
  sort = {dir: 'asc', field: 'name'},
  filters,
  limit = 25,
}: Omit<Partial<ListActiveSubstancesInput>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters: {status: ['active'] as const, ...filters}, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [
      sort?.dir,
      sort?.field,
      filters?.categories,
      filters?.calculationMethod,
      filters?.unit,
      filters?.search,
      limit,
      search,
    ]
  )

  const getPageQuery: GetPageQuery<DrugCatalogItem, ActiveSubstanceStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {
              id: start.id,
              direction: dir,
              createdAt: start.createdAt,
              name: start.name,
            }
          : undefined

      return {
        query: queries.drugCatalog.listActiveSubstances({
          cursor,
          limit: pageLimit,
          sort,
          filters: {status: ['active'] as const, ...filters, ...(search ? {search} : {})},
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getSingleQuery: GetSingleQuery<DrugCatalogItem> = useCallback(
    ({id}) => ({
      query: queries.drugCatalog.getActiveSubstanceById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: DrugCatalogItem): ActiveSubstanceStartRow => ({
      id: row.id,
      createdAt: row.createdAt,
      name: row.name,
    }),
    []
  )

  const transformToSelectItem = useCallback((row: DrugCatalogItem) => ({label: row.name, value: row.id}), [])

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
